import React from 'react';
import {useParagraphsContext} from '../ParagraphsContext';
import {useUserContext} from 'authentication/UserContext';
import {KeywordAssignment, KeywordStatus} from 'types';
import _ from 'lodash';
import {useKeywordAssignmentsFormDataQuery} from './keywordAssignmentFromData.generated';
import {numericalSort} from 'helpers/sortHelpers';

const getVexClusterIdsFromAssignment = (kwa: KeywordAssignment) =>
	kwa.vexClusterAssignments.map(vca => vca.vexCluster.id);

const getVexClustersFromAssignment = (kwa: KeywordAssignment) =>
	kwa.vexClusterAssignments.map(vca => vca.vexCluster);

export const useKeywordAssignmentsFormData = (
	selectedKeywordIds: string[] | undefined,
) => {
	const {selectedParagraphs} = useParagraphsContext();
	const {myVexClusters} = useUserContext();

	const {data} = useKeywordAssignmentsFormDataQuery();
	const keywords = React.useMemo(
		() => numericalSort(data?.keywords ?? [], 'sortPriority'),
		[data],
	);

	// Exclude a keyword to suggest additionally, iff every paragraph contains it
	const additionalKeywordOptions = React.useMemo(
		() =>
			keywords.filter(k => {
				return !selectedParagraphs.every(p => {
					return p.keywords.some(pk => pk.id === k.id);
				});
			}),
		[keywords, selectedParagraphs],
	);

	const myVexClusterIds = React.useMemo(
		() => myVexClusters.map(vc => vc.id),
		[myVexClusters],
	);

	const myKeywordAssignmentsWithMyVexAssignments = React.useMemo(() => {
		const allKeywordAssignments =
			selectedParagraphs
				?.flatMap(p => p.keywordAssignments ?? [])
				.filter(kwa =>
					kwa.vexClusterAssignments.some(vca =>
						myVexClusterIds.includes(vca.vexCluster.id),
					),
				)
				.map(kwa => ({
					...kwa,
					vexClusterAssignments: kwa.vexClusterAssignments.filter(vca =>
						myVexClusterIds.includes(vca.vexCluster.id),
					),
				})) ?? [];

		const uniqueKeywordAssignments = _.uniqBy(
			allKeywordAssignments,
			kwa =>
				kwa.keyword.id +
				kwa.vexClusterAssignments
					.map(vca => `${vca.vexCluster.id}${vca.keywordStatus}`)
					.join(''),
		);

		return uniqueKeywordAssignments;
	}, [selectedParagraphs, myVexClusterIds]);

	const defaultKeywordAssignment: KeywordAssignment | undefined =
		React.useMemo(() => {
			for (const status of [
				KeywordStatus.New,
				KeywordStatus.Declined,
				KeywordStatus.Finalized,
			]) {
				const defaultKeywordAssignment =
					myKeywordAssignmentsWithMyVexAssignments.find(kwa =>
						kwa.vexClusterAssignments.some(vca => vca.keywordStatus === status),
					);
				if (defaultKeywordAssignment) {
					return defaultKeywordAssignment;
				}
			}
			return myKeywordAssignmentsWithMyVexAssignments.find(() => true);
		}, [myKeywordAssignmentsWithMyVexAssignments]);

	const defaultKeyword = React.useMemo(() => {
		if (defaultKeywordAssignment) {
			return defaultKeywordAssignment.keyword;
		}
	}, [defaultKeywordAssignment]);

	const defaultVexCluster = React.useMemo(() => {
		let keywordAssignment: KeywordAssignment | undefined;

		if (defaultKeywordAssignment) {
			keywordAssignment = defaultKeywordAssignment;
		}

		if (selectedKeywordIds?.length) {
			keywordAssignment = myKeywordAssignmentsWithMyVexAssignments.find(kwa =>
				selectedKeywordIds.includes(kwa.keyword.id),
			);
		}

		if (keywordAssignment) {
			for (const status of [
				KeywordStatus.New,
				KeywordStatus.Declined,
				KeywordStatus.Finalized,
			]) {
				const vexCluster = keywordAssignment.vexClusterAssignments.find(
					vca => vca.keywordStatus === status,
				)?.vexCluster;
				if (vexCluster) {
					return vexCluster;
				}
			}
			return myKeywordAssignmentsWithMyVexAssignments.find(() => true)
				?.vexClusterAssignments[0]?.vexCluster;
		}
	}, [
		selectedKeywordIds,
		defaultKeywordAssignment,
		myKeywordAssignmentsWithMyVexAssignments,
	]);

	const selectableKeywords = React.useMemo(() => {
		const keywords = _.uniqBy(
			myKeywordAssignmentsWithMyVexAssignments.map(kwa => kwa.keyword),
			k => k.id,
		);

		return keywords.map(k => ({
			key: k.id,
			id: k.id,
			name: k.name,
		}));
	}, [myKeywordAssignmentsWithMyVexAssignments]);

	const selectableVexClusters = React.useMemo(() => {
		if (!selectedKeywordIds?.length) return [];

		// Build a set of vex cluster IDs for each selected keyword
		const vexIdsByKeyword = selectedKeywordIds.map(kwId => {
			const assignments = myKeywordAssignmentsWithMyVexAssignments.filter(
				kwa => kwa.keyword.id === kwId,
			);
			const vcIds = assignments.flatMap(getVexClusterIdsFromAssignment);
			return new Set(vcIds);
		});

		// Only keep vex clusters present in every keyword's set (intersection)
		const matchingAssignments = myKeywordAssignmentsWithMyVexAssignments.filter(
			kwa => selectedKeywordIds.includes(kwa.keyword.id),
		);
		const allVexClusters = matchingAssignments.flatMap(
			getVexClustersFromAssignment,
		);

		const uniqueVexClusters = _.uniqBy(allVexClusters, vc => vc.id).filter(vc =>
			vexIdsByKeyword.some(set => set.has(vc.id)),
		);

		return uniqueVexClusters.map(vc => ({
			key: vc.id,
			id: vc.id,
			name: vc.name,
		}));
	}, [selectedKeywordIds, myKeywordAssignmentsWithMyVexAssignments]);

	// All user vex clusters present in the paragraph assignments (no keyword filter)
	const defaultVexClusterValues = React.useMemo(() => {
		const newVcAssignments = myKeywordAssignmentsWithMyVexAssignments.flatMap(
			kwa =>
				kwa.vexClusterAssignments.filter(
					vca => vca.keywordStatus === KeywordStatus.New,
				),
		);
		const uniqueVc = _.uniqBy(newVcAssignments, vca => vca.vexCluster.id);
		return uniqueVc.map(vca => ({
			key: vca.vexCluster.id,
			id: vca.vexCluster.id,
			name: vca.vexCluster.name,
		}));
	}, [myKeywordAssignmentsWithMyVexAssignments]);

	// Keywords that have at least one "New" vex cluster assignment
	const defaultKeywordValues = React.useMemo(() => {
		const defaultVcIds = new Set(defaultVexClusterValues.map(vc => vc.key));
		if (!defaultVcIds.size) return [];
		const matchingKeywords = _.uniqBy(
			myKeywordAssignmentsWithMyVexAssignments,
			kwa => kwa.keyword.id,
		).filter(kwa => {
			const newVcIds = new Set(
				kwa.vexClusterAssignments
					.filter(vca => vca.keywordStatus === KeywordStatus.New)
					.map(vca => vca.vexCluster.id),
			);
			return [...defaultVcIds].some(id => newVcIds.has(id));
		});
		return matchingKeywords.map(kwa => ({
			key: kwa.keyword.id,
			id: kwa.keyword.id,
			name: kwa.keyword.name,
		}));
	}, [defaultVexClusterValues, myKeywordAssignmentsWithMyVexAssignments]);

	return {
		additionalKeywordOptions,
		defaultKeyword,
		defaultVexCluster,
		selectableKeywords,
		selectableVexClusters,
		defaultVexClusterValues,
		defaultKeywordValues,
	};
};
